import { Typography } from '@euler/app/components/typography';
import { couponBenefitText } from '@euler/functions/apdm/couponRedemption';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { OrgMarketingFollowupFeedPayload } from '@euler/model/apdm';
import { formatDateTime } from '@euler/utils/formatters/datetime';
import { memo } from 'react';
import { View } from 'react-native';
import { designRoles } from '../common/designRoles';

type Coupon = NonNullable<
  OrgMarketingFollowupFeedPayload['offer']['currentCoupon']
>;

/**
 * 当前优惠: the campaign's coupon (its title and tagline, an item a row with
 * its benefit and count, its validity), then the shop's own offers. Nothing
 * when there is neither.
 */
export const OfferSection = memo(
  ({ coupon, texts }: { coupon: Coupon | null; texts: readonly string[] }) => {
    const { rpx } = useResponsiveContext();
    if (!coupon && !texts.length) return null;
    return (
      <View style={{ marginTop: rpx(14) }}>
        <Typography.Text
          size={rpx(12)}
          color={designRoles.textSecondary}
          medium
        >
          当前优惠
        </Typography.Text>
        {coupon ? (
          <>
            <Typography.Text
              size={rpx(14)}
              lineHeight={rpx(21)}
              color={designRoles.textPrimary}
              medium
              mt={rpx(4)}
            >
              {coupon.title}
            </Typography.Text>
            {coupon.tagline ? (
              <Typography.Text size={rpx(13)} color={designRoles.textSecondary}>
                {coupon.tagline}
              </Typography.Text>
            ) : null}
            <View style={{ marginTop: rpx(6), gap: rpx(4) }}>
              {coupon.items.map((item, index) => (
                <View
                  key={index}
                  style={{ flexDirection: 'row', gap: rpx(12) }}
                >
                  <Typography.Text
                    size={rpx(13)}
                    color={designRoles.textPrimary}
                    style={{ flex: 1 }}
                  >
                    {item.label}
                  </Typography.Text>
                  <Typography.Text
                    size={rpx(13)}
                    color={designRoles.textSecondary}
                  >
                    {`${couponBenefitText(item.benefit)} · ${item.qty}${
                      item.qtyUnit ?? '次'
                    }`}
                  </Typography.Text>
                </View>
              ))}
            </View>
            <Typography.Text
              size={rpx(12)}
              color={designRoles.textTertiary}
              mt={rpx(6)}
            >
              {/* With its year: a campaign can run into the next one. */}
              {`有效期 ${formatDateTime(coupon.startsAt)} 至 ${formatDateTime(
                coupon.endsAt,
              )}`}
            </Typography.Text>
          </>
        ) : null}
        {texts.map((text, index) => (
          <Typography.Text
            key={index}
            size={rpx(13)}
            lineHeight={rpx(19)}
            color={designRoles.textPrimary}
            mt={rpx(4)}
          >
            {text}
          </Typography.Text>
        ))}
      </View>
    );
  },
);
